import { useMemo } from 'react';

import { TNavigationItem } from '../types';

type TUseNavigationToolkitItemProps<T extends TNavigationItem> = {
  items: T[];
  activeItem: T | null;
  onChangeData: (data: T) => void;
  isItemDisabled?: (item: T) => boolean;
};

type TUseNavigationToolkitItemReturn<T> = {
  activeItem: T | null;
  hasNextItem: boolean;
  hasPreviousItem: boolean;
  handleNextItem: () => void;
  handleNextItemWithoutIncidence: () => void;
  handlePreviousItem: () => void;
};

export function useNavigationToolkitItem<T extends TNavigationItem>({
  items,
  activeItem,
  onChangeData,
  isItemDisabled,
}: TUseNavigationToolkitItemProps<T>): TUseNavigationToolkitItemReturn<T> {
  const flattenedItems = useMemo(() => {
    const flatten = (items: T[]): T[] => {
      return items.reduce((acc, item) => {
        if (item?.children && Array.isArray(item.children) && item.children.length > 0) {
          acc.push(...flatten(item.children as T[]));
        } else {
          acc.push(item);
        }

        return acc;
      }, [] as T[]);
    };

    return flatten(items);
  }, [items]);

  const handleNextItem = () => {
    const currentIndex = flattenedItems.findIndex(item => item.id === activeItem?.id);
    const nextIndex = currentIndex + 1;

    if (nextIndex < flattenedItems.length) {
      onChangeData(flattenedItems[nextIndex]);
    }
  };

  const handleNextItemWithoutIncidence = () => {
    const currentIndex = flattenedItems.findIndex(item => item.id === activeItem?.id);

    const nextIndex = flattenedItems.findIndex(
      (item, index) => index > currentIndex && !isItemDisabled?.(item)
    );

    if (nextIndex !== -1) {
      onChangeData(flattenedItems[nextIndex]);
    }
  };

  const handlePreviousItem = () => {
    const currentIndex = flattenedItems.findIndex(item => item.id === activeItem?.id);
    const previousIndex = currentIndex - 1;

    if (previousIndex >= 0) {
      onChangeData(flattenedItems[previousIndex]);
    }
  };

  const hasNextItem = useMemo(() => {
    const currentIndex = flattenedItems.findIndex(item => item.id === activeItem?.id);
    return currentIndex + 1 < flattenedItems.length;
  }, [flattenedItems, activeItem]);

  const hasPreviousItem = useMemo(() => {
    const currentIndex = flattenedItems.findIndex(item => item.id === activeItem?.id);
    return currentIndex - 1 >= 0;
  }, [flattenedItems, activeItem]);

  return {
    activeItem,
    hasNextItem,
    hasPreviousItem,
    handleNextItem,
    handleNextItemWithoutIncidence,
    handlePreviousItem,
  };
}
